import { CascadiaColor, CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useState } from "react";
import { ButtonProps } from "react-aria-components";

import { TagElement } from "./TagElement";
import { TAG_SIZES } from "./constants";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";
import { ImageAvatar } from "../avatar/ImageAvatar";
import { accessibleTouchSize, useGetAllFocusShadows } from "../utils";

export interface TagProps extends Omit<ButtonProps, "type"> {
  color?: CascadiaColor;
  fill?: CascadiaColor;
  type?: "bold" | "subtle";
  isButton?: boolean;
  onPress?: () => void;
  onDismiss?: () => void;
  children?: React.ReactNode;
  testID?: string;
  size?: TagSize;
  shape?: TagShape;
  leftIcon?: CascadiaStandardIcon;
  statusInfoIcon?: boolean;
  avatarURL?: string;
}

export type TagType = Omit<TagProps, "fill" | "color">;
export type TagSize = "small" | "medium";
export type TagShape = "default" | "pill";

export function _Tag(props: TagProps) {
  const {
    color = "text-inverse",
    children,
    fill,
    onDismiss,
    testID = "tag",
    size = "small",
    shape = "default",
    leftIcon,
    statusInfoIcon = false,
    avatarURL,
    type,
    ...buttonProps
  } = props;
  const [isDismissed, setIsDismissed] = useState(false);
  const colors = useColors();
  const getAllFocusShadows = useGetAllFocusShadows();

  const tagSize = leftIcon || avatarURL ? "medium" : size;
  const tagShape = avatarURL ? "avatar" : shape;

  const sizeKey = (tagShape + "_" + tagSize) as keyof typeof TAG_SIZES;
  const sizeTokens = TAG_SIZES[sizeKey];

  const onPress = () => {
    if (onDismiss) {
      setIsDismissed(true);
      onDismiss();
    }

    if (buttonProps.onPress) {
      buttonProps.onPress();
    }
  };

  const isButton =
    props.isButton || Boolean(props.onPress) || Boolean(onDismiss);

  if (isDismissed) return null;
  return (
    <TagElement
      buttonProps={isButton ? { ...buttonProps, onPress } : undefined}
      testID={testID}
      className={css(
        styles.tag,
        {
          color: color ? colors[color] : undefined,
          background: fill ? colors[fill] : undefined,
          cursor: isButton ? "pointer" : undefined,
          "&:focus-within": isButton ? getAllFocusShadows() : undefined,
        },
        sizeTokens
      )}
    >
      {avatarURL && (
        <ImageAvatar
          size="x-small"
          image={
            <img
              style={{ transform: "scale(1.25)" }}
              alt="profile"
              src={avatarURL}
            />
          }
        />
      )}
      {leftIcon && <Icon name={leftIcon} size="space-4" color={color} />}
      <Text style="global/label-small" color={color}>
        {children}
      </Text>
      {statusInfoIcon && <Icon name="info" size="space-4" color={color} />}
      {onDismiss && (
        <Icon type="mini" name="close" size="space-3" color={color} />
      )}
    </TagElement>
  );
}

const styles = {
  tag: css({
    all: "unset",
    position: "relative",
    display: "flex",
    gap: "var(--space-1)",
    alignItems: "center",
    width: "fit-content",
    boxSizing: "border-box",
    "&:after": accessibleTouchSize,
    flexShrink: 0,
    "> span": {
      whiteSpace: "nowrap",
    },
  }),
};
